import { useContext, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { orderBy } from 'lodash';
import { DATE_FORMATS } from '@callmart/types';
import { getAllCapacityReportsForPeriod } from '../../services/capacityReport';
import UserContext from '../../store/User/UserContext';
import { CapacityReportType } from '../../enums/CapacityReportType';
import { getPerformedCapacityReport } from '../../services/performedCapacityReport';
import SelectExportDateRangeDialog from '../Dialogs/SelectExportDateRangeDialog';
import { getDateOptionsForExportDialog } from './helpers/utils';
import dayjs from '@utils/dayjs';

const exportColumnNames = [
    'Stadt',
    'Worktype',
    'Channel',
    'Task',
    'Monat',
    'Capacity Name',
    'Einheit',
    'Capacity Value',
];

function SelectExportCapacitiesDateRangeDialog({ locations, open, onClose, reportType }) {
    const { t } = useTranslation('allDeltas');
    const loggedInUser = useContext(UserContext);

    const dialogTitle = t('export.dialogTitle', {
        reportName: reportType === CapacityReportType.CAPACITY_REPORT ? t('report.capacity') : t('report.performed'),
    });
    const { previousMonths: startDateOptions, nextMonths: endDateOptions } = getDateOptionsForExportDialog({
        reportType,
    });
    const startDate =
        reportType === CapacityReportType.CAPACITY_REPORT
            ? startDateOptions[0].month
            : startDateOptions[startDateOptions.length - 1].month;
    const endDate =
        reportType === CapacityReportType.CAPACITY_REPORT ? endDateOptions[1].month : endDateOptions[0].month;

    const [selectedStartDate, setSelectedStartDate] = useState(startDate);
    const [selectedEndDate, setSelectedEndDate] = useState(endDate);

    const getDataFromDb = async () => {
        const dateFrom = dayjs.utc(selectedStartDate).startOf('month');
        const dateTo = dayjs.utc(selectedEndDate).endOf('month');

        // WFM (via isTEFUser) and WTM both export all locations read-only.
        const location =
            loggedInUser?.isTEFUser || loggedInUser?.isWTMUser ? null : locations.map((location) => location.id);

        if (reportType === CapacityReportType.PERFORMED_CAPACITY_REPORT) {
            return await getPerformedCapacityReport({ location, dateFrom, dateTo });
        }

        return await getAllCapacityReportsForPeriod({ dateFrom, dateTo, location });
    };

    const getExportData = (data) => {
        const exportData = [];
        data.forEach((capacity) => {
            const location = locations.find(
                (location) => location.id === capacity.workingUnitLocation.worktypeLocation.locationId
            );

            let reports =
                reportType === CapacityReportType.PERFORMED_CAPACITY_REPORT
                    ? capacity.performedCapacityReports
                    : capacity.capacityReports;
            reports = orderBy(reports, ['date'], ['asc']);
            reports.forEach((capacityReport) => {
                exportData.push({
                    capacityId: capacity.id,
                    capacityTypeId: capacity.capacityTypeId,
                    capacityTypeName: capacity.capacityType.name,
                    capacityTypeUnit: capacity.capacityType.unit,
                    capacityReportValue: capacityReport?.value ?? null,
                    code: location?.code ?? null,
                    city: location?.city ?? null,
                    worktype: capacity?.workingUnitLocation?.workingUnit?.worktype?.worktype,
                    channel: capacity?.workingUnitLocation?.workingUnit?.channel?.name,
                    task: capacity?.workingUnitLocation?.workingUnit?.task?.name,
                    date: capacityReport.date,
                });
            });
        });

        return exportData;
    };

    const createExcelRowForExport = (exportDataRow) => {
        return [
            exportDataRow.code + ' - ' + exportDataRow.city,
            exportDataRow.worktype,
            exportDataRow.channel,
            exportDataRow.task,
            dayjs(exportDataRow.date).startOf('month').format(DATE_FORMATS.DATE),
            exportDataRow.capacityTypeName,
            exportDataRow.capacityTypeUnit,
            exportDataRow.capacityReportValue,
        ];
    };

    return (
        <SelectExportDateRangeDialog
            open={open}
            onClose={onClose}
            selectedStartDate={selectedStartDate}
            onStartDateChange={setSelectedStartDate}
            selectedEndDate={selectedEndDate}
            onEndDateChange={setSelectedEndDate}
            startDateOptions={startDateOptions}
            endDateOptions={endDateOptions}
            createExcelRowForExport={createExcelRowForExport}
            getExportData={getExportData}
            getDataFromDb={getDataFromDb}
            createRowFromExportData={createExcelRowForExport}
            exportedDataType={
                reportType === CapacityReportType.PERFORMED_CAPACITY_REPORT
                    ? 'Perfomed_Capacities_Report'
                    : 'Capacities_Report'
            }
            dialogTitle={dialogTitle}
            exportColumnNames={exportColumnNames}
            dialogText={t('export.dialogText')}
        ></SelectExportDateRangeDialog>
    );
}

export default SelectExportCapacitiesDateRangeDialog;
